import { createSlice } from '@reduxjs/toolkit';

const ARRAY_FILTER_KEYS = ['amenities', 'views', 'stay_styles', 'ideal_for'];

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export const normalizeHomeSearchParams = (params = {}) => {
  const normalized = isPlainObject(params) ? { ...params } : {};
  ARRAY_FILTER_KEYS.forEach((key) => {
    normalized[key] = Array.isArray(normalized[key]) ? normalized[key] : [];
  });
  return normalized;
};

export const isValidSavedHomeSearchState = (state) => {
  if (!isPlainObject(state) || !isPlainObject(state.searchParams) || !Array.isArray(state.results)) {
    return false;
  }

  if (state.isSearchActive !== true || !Number.isInteger(state.currentPage) || state.currentPage < 0) {
    return false;
  }

  if (ARRAY_FILTER_KEYS.some((key) => (
    state.searchParams[key] !== undefined && !Array.isArray(state.searchParams[key])
  ))) {
    return false;
  }

  return state.results.every((listing) => (
    isPlainObject(listing)
    && typeof listing.id === 'string'
    && typeof listing.title === 'string'
    && (listing.amenities_preview === undefined || Array.isArray(listing.amenities_preview))
    && (listing.amenities === undefined || Array.isArray(listing.amenities))
  ));
};

const initialState = {
  searchParams: {
    checkIn: '',
    checkOut: '',
    category: '',
    emirate: '',
    area: '',
    bedrooms: '',
    guests: '',
    adults: '',
    children: '',
    allow_extra_bed: '',
    min_price: '',
    price_max: '',
    amenities: [],
    views: [],
    stay_styles: [],
    ideal_for: [],
    with_balcony: '',
    most_affordable: '',
  },
  sortBy: 'newest', // newest, price_low, price_high, bedrooms
  results: [],
  isSearchActive: false,
  isLoading: false,
  hasMore: true,
  currentPage: 0,
  pageSize: 6,
  error: null,
  recentSearches: [], // Store recent search queries
};

const homeSearchSlice = createSlice({
  name: 'homeSearch',
  initialState,
  reducers: {
    setSearchParams: (state, action) => {
      state.searchParams = normalizeHomeSearchParams({ ...state.searchParams, ...action.payload });
      // Check if any search param (excluding dates) has a value
      // Dates don't count as "active search" since they're for enquiry form only
      const { checkIn, checkOut, ...searchFilters } = state.searchParams;
      state.isSearchActive = Object.values(searchFilters).some(val =>
        Array.isArray(val) ? val.length > 0 : val !== '' && val !== null
      );
      // Set loading to true immediately when search becomes active
      // This prevents showing empty results during the debounce period
      if (state.isSearchActive) {
        state.isLoading = true;
      }
    },
    // Update dates only - doesn't trigger search, just stores for enquiry form pre-fill
    setSelectedDates: (state, action) => {
      state.searchParams.checkIn = action.payload.checkIn || '';
      state.searchParams.checkOut = action.payload.checkOut || '';
    },
    setSearchResults: (state, action) => {
      const { results, append, hasMore } = action.payload;
      if (append) {
        state.results = [...state.results, ...results];
      } else {
        state.results = results;
        state.currentPage = 0;
      }
      // Use provided hasMore value, or calculate it based on results length
      state.hasMore = hasMore !== undefined ? hasMore : results.length === state.pageSize;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    incrementPage: (state) => {
      state.currentPage += 1;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
      // Reset pagination when sort changes
      state.currentPage = 0;
    },
    addRecentSearch: (state, action) => {
      const search = action.payload;
      // Remove if already exists
      state.recentSearches = state.recentSearches.filter(s => s.query !== search.query);
      // Add to beginning
      state.recentSearches.unshift(search);
      // Keep only last 5
      state.recentSearches = state.recentSearches.slice(0, 5);
    },
    clearRecentSearches: (state) => {
      state.recentSearches = [];
    },
    clearSearch: (state) => {
      state.searchParams = initialState.searchParams;
      state.sortBy = 'newest';
      state.results = [];
      state.isSearchActive = false;
      state.isLoading = false;
      state.currentPage = 0;
      state.hasMore = true;
      state.error = null;
    },
    restoreSearchFromStorage: (state, action) => {
      const savedState = action.payload;
      if (isValidSavedHomeSearchState(savedState)) {
        state.searchParams = normalizeHomeSearchParams(savedState.searchParams);
        state.results = savedState.results;
        state.isSearchActive = true;
        state.currentPage = savedState.currentPage;
      }
    },
  },
});

export const {
  setSearchParams,
  setSelectedDates,
  setSearchResults,
  setLoading,
  setError,
  incrementPage,
  setSortBy,
  addRecentSearch,
  clearRecentSearches,
  clearSearch,
  restoreSearchFromStorage,
} = homeSearchSlice.actions;

export default homeSearchSlice.reducer;
